import React from "react";
import { Box, SxProps, Theme } from "@mui/material";
import { tokens } from "./design-tokens";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";

interface GlassContainerProps {
  children: React.ReactNode;
  sx?: SxProps<Theme>;
  blur?: number;
  radius?: number;
  dontHoverPop?: boolean;
  isMetallic?: boolean;
  isDark?: Boolean;
}

/**
 * Enterprise liquid-glass container.
 *
 * Layers (bottom → top):
 *  1. backdrop-filter: blur + saturate  (frosted glass)
 *  2. Semi-transparent fill              (tinted surface)
 *  3. Inner-glow border via box-shadow   (edge highlight)
 *  4. Top-edge specular highlight        (::before pseudo)
 *  5. Subtle noise overlay               (::after pseudo)
 */
const GlassContainer: React.FC<GlassContainerProps> = ({
  children,
  sx = {},
  blur = 18,
  radius = 20,
  dontHoverPop = false,
  isMetallic = false,
  isDark: isDarkProp,
}) => {
  const dark = useSelector((state: RootState) => state.partners.darkMode);

  // ── Background ───────────────────────────────────────
  // NEUTRAL palettes — solid black in dark mode for contrast
  const bg = isMetallic
    ? dark
      ? "linear-gradient(145deg, rgba(10,10,14,0.96) 0%, rgba(6,6,10,0.98) 50%, rgba(12,12,18,0.96) 100%)"
      : "linear-gradient(145deg, rgba(255,255,255,0.9) 0%, rgba(240,240,245,0.85) 50%, rgba(225,225,230,0.8) 100%)"
    : dark
      ? "linear-gradient(135deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.75) 100%)"
      : "linear-gradient(135deg, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.25) 100%)";

  // ── Inner-glow border (box-shadow inset) ─────────────
  const innerGlow = dark
    ? "inset 0 0 0 1px rgba(255,255,255,0.08), inset 0 1px 0 rgba(255,255,255,0.10)"
    : "inset 0 0 0 1px rgba(255,255,255,0.45), inset 0 1px 0 rgba(255,255,255,0.65)";

  // ── Outer shadow ─────────────────────────────────────
  const outerShadow = isMetallic
    ? dark
      ? "0 8px 32px rgba(0,0,0,0.5)"
      : "0 8px 32px rgba(0,0,0,0.12)"
    : tokens.shadow.glass;

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        background: bg,
        backdropFilter: `blur(${blur}px) saturate(180%)`,
        WebkitBackdropFilter: `blur(${blur}px) saturate(180%)`,
        borderRadius: `${radius}px`,
        border: "none",
        boxShadow: `${innerGlow}, ${outerShadow}`,
        transition: "transform 0.3s ease, box-shadow 0.3s ease",
        ...(!dontHoverPop && {
          "&:hover": {
            transform: "translateY(-1px)",
            boxShadow: `${innerGlow}, ${tokens.shadow.glassHover}`,
          },
        }),

        // ── Specular top-edge highlight (::before) ───────
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "50%",
          background: dark
            ? "linear-gradient(180deg, rgba(255,255,255,0.04) 0%, transparent 100%)"
            : "linear-gradient(180deg, rgba(255,255,255,0.35) 0%, transparent 100%)",
          borderRadius: `${radius}px ${radius}px 0 0`,
          pointerEvents: "none",
          zIndex: 0,
        },

        // ── Subtle noise texture (::after) ───────────────
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.03'/%3E%3C/svg%3E")`,
          backgroundRepeat: "repeat",
          backgroundSize: "128px 128px",
          borderRadius: `${radius}px`,
          pointerEvents: "none",
          zIndex: 0,
          opacity: dark ? 0.4 : 0.3,
        },

        // Ensure children sit above pseudo-elements
        "& > *": {
          position: "relative",
          zIndex: 1,
        },

        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

export default GlassContainer;
